import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { VehicleDetails, FormSections } from "@/types/vehicleDetails";
import { validateRegistrationFormatAny } from "@/utils/registrationValidation";

export const useVehicleForm = (
  initialData: Partial<VehicleDetails>,
  onChange: (details: Partial<VehicleDetails>) => void,
  isAutoLoaded: boolean = false,
  onAutoLoadedFieldEditAttempt?: () => void,
  isManualMode?: boolean,
  regNumber?: string
) => {
  // Track if we've initialized from initialData to prevent re-initialization
  const hasInitializedRef = useRef(false);
  // Track if user manually opened a section to prevent auto-close
  const manuallyOpenedSectionRef = useRef<keyof FormSections | null>(null);
  // Track if we should notify parent of changes (to avoid calling onChange during render)
  const shouldNotifyParentRef = useRef(false);

  // Initialize from initialData ONCE, but NEVER sync afterwards to avoid overwriting user input
  const [vehicleDetails, setVehicleDetails] = useState<Partial<VehicleDetails>>(
    () => {
      hasInitializedRef.current = true;
      return initialData;
    }
  );

  // CRITICAL: DO NOT sync from initialData after mount - it overwrites user input!
  // The summary card reads from vehicleDetails which is updated directly via handleSelectChange
  // When user changes make/model/year, handleSelectChange updates vehicleDetails AND calls onChange
  // onChange updates parent state, but we DON'T sync back because that would overwrite user input
  // Initialize open sections based on whether data is auto-loaded
  const [openSections, setOpenSections] = useState<FormSections>(() => {
    // If auto-loaded with complete vehicle info, start with details open
    if (
      isAutoLoaded &&
      initialData.make &&
      initialData.model &&
      initialData.year
    ) {
      const hasAllDetails =
        initialData.fuelType &&
        (initialData.fuelType === "El" ||
          initialData.fuelType === "Elbil" ||
          initialData.fuelType === "ELECTRIC" ||
          initialData.engine) &&
        (initialData.vehicleType === "mc" || initialData.bodyType) &&
        initialData.transmission &&
        initialData.color &&
        initialData.variant;

      return {
        makeModel: false,
        details: !hasAllDetails, // Open details if color is missing
        tires: false,
        equipment: !!hasAllDetails, // Open equipment when all details are complete (will show even if empty)
        final: false,
      };
    }
    // Default: start with makeModel open
    return {
      makeModel: true,
      details: false,
      tires: false,
      equipment: false,
      final: false,
    };
  });

  // CRITICAL: Don't sync from initialData to vehicleDetails as it overwrites user input
  // Instead, onChange callback handles updating parent state, which updates initialData
  // The summary card reads from vehicleDetails which is updated via handleSelectChange

  // EXCEPTION 1: When isAutoLoaded becomes true, sync ALL fields from initialData
  // This ensures that when AutoUncle data is loaded, all fields are populated in the form
  // IMPORTANT: This does NOT call onChange to avoid React warnings about setState during render
  const prevIsAutoLoadedRef = useRef(isAutoLoaded);
  const prevInitialDataRef = useRef(initialData);

  useEffect(() => {
    // DEBUG: Uncomment for debugging state sync issues
    // console.log('[useVehicleForm] 🔄 useEffect triggered:', {
    //   isAutoLoaded,
    //   prevIsAutoLoaded: prevIsAutoLoadedRef.current,
    //   hasInitialized: hasInitializedRef.current,
    //   initialData: {
    //     make: initialData.make,
    //     model: initialData.model,
    //     year: initialData.year,
    //     fuelType: initialData.fuelType,
    //     bodyType: initialData.bodyType,
    //     color: initialData.color,
    //     transmission: initialData.transmission,
    //     variant: initialData.variant,
    //     engineVolume: initialData.engineVolume,
    //   },
    //   prevInitialData: {
    //     make: prevInitialDataRef.current.make,
    //     model: prevInitialDataRef.current.model,
    //     year: prevInitialDataRef.current.year,
    //     fuelType: prevInitialDataRef.current.fuelType,
    //     bodyType: prevInitialDataRef.current.bodyType,
    //     color: prevInitialDataRef.current.color,
    //     transmission: prevInitialDataRef.current.transmission,
    //   },
    //   currentVehicleDetails: {
    //     make: vehicleDetails.make,
    //     model: vehicleDetails.model,
    //     year: vehicleDetails.year,
    //     fuelType: vehicleDetails.fuelType,
    //     bodyType: vehicleDetails.bodyType,
    //     color: vehicleDetails.color,
    //     transmission: vehicleDetails.transmission,
    //   },
    // });

    // Only sync when isAutoLoaded changes from false to true (initial load)
    // OR when initialData changes significantly (e.g., AutoUncle data arrives)
    const isAutoLoadTriggered = !prevIsAutoLoadedRef.current && isAutoLoaded;

    // Check if initialData has ANY meaningful data
    const hasAnyDataInInitial =
      initialData.make ||
      initialData.model ||
      initialData.year ||
      initialData.fuelType ||
      initialData.bodyType ||
      initialData.color ||
      initialData.transmission ||
      initialData.variant ||
      initialData.engineVolume;

    const currentStateIsEmpty =
      !vehicleDetails.make && !vehicleDetails.model && !vehicleDetails.year;

    const hasSignificantDataChange =
      hasInitializedRef.current &&
      isAutoLoaded &&
      hasAnyDataInInitial &&
      // Check if initialData has more complete data than current state
      (initialData.make !== prevInitialDataRef.current.make ||
        initialData.model !== prevInitialDataRef.current.model ||
        initialData.year !== prevInitialDataRef.current.year ||
        initialData.fuelType !== prevInitialDataRef.current.fuelType ||
        initialData.bodyType !== prevInitialDataRef.current.bodyType ||
        initialData.color !== prevInitialDataRef.current.color ||
        initialData.transmission !== prevInitialDataRef.current.transmission ||
        initialData.variant !== prevInitialDataRef.current.variant ||
        initialData.engineVolume !== prevInitialDataRef.current.engineVolume);

    const shouldSync =
      isAutoLoadTriggered ||
      hasSignificantDataChange ||
      (isAutoLoaded && hasAnyDataInInitial && currentStateIsEmpty);

    // DEBUG: Uncomment for debugging sync issues
    // console.log('[useVehicleForm] 🔍 Sync check:', {
    //   isAutoLoadTriggered,
    //   hasSignificantDataChange,
    //   hasAnyDataInInitial,
    //   currentStateIsEmpty,
    //   willSync: shouldSync,
    // });

    // Sync if: auto-load triggered, significant data change, OR if we have data in initialData but empty state
    if (shouldSync) {
      // When AutoUncle data is first loaded, sync all fields from initialData
      // Use setTimeout to ensure this happens after render, avoiding React warnings
      setTimeout(() => {
        // DEBUG: Uncomment for debugging sync execution
        // console.log('[useVehicleForm] ⏰ setTimeout callback executing, syncing data...');
        setVehicleDetails((prev) => {
          // Merge initialData with existing state, but preserve user edits
          // Only update fields that are in initialData and different from current state
          const updates: Partial<VehicleDetails> = {};

          // Sync all fields from initialData when AutoUncle data is loaded
          // But only if current state is empty or incomplete
          const needsFullSync = !prev.make || !prev.model || !prev.year;

          // DEBUG: Uncomment for debugging sync decisions
          // console.log('[useVehicleForm] 📊 Sync decision:', {
          //   needsFullSync,
          //   isAutoLoadTriggered,
          //   hasAnyDataInInitial,
          //   currentStateIsEmpty,
          //   prevState: {
          //     make: prev.make,
          //     model: prev.model,
          //     year: prev.year,
          //   },
          // });

          // Always do full sync when auto-loaded and state is empty or incomplete
          // This ensures ALL data from AutoUncle is mapped correctly
          const shouldDoFullSync =
            needsFullSync ||
            isAutoLoadTriggered ||
            (isAutoLoaded && hasAnyDataInInitial && currentStateIsEmpty);

          if (shouldDoFullSync) {
            // Full sync: update ALL fields from initialData
            // This ensures 100% mapping when AutoUncle data arrives
            // DEBUG: Uncomment for debugging
            // console.log('[useVehicleForm] 🔄 Performing FULL sync - mapping ALL fields from initialData');
            (Object.keys(initialData) as Array<keyof VehicleDetails>).forEach(
              (key) => {
                const value = initialData[key];
                // Update if value exists and is not empty string
                if (
                  value !== undefined &&
                  value !== null &&
                  (typeof value !== "string" || value !== "") &&
                  (typeof value !== "object" ||
                    !Array.isArray(value) ||
                    value.length > 0)
                ) {
                  (updates as any)[key] = value;
                }
              }
            );

            // Also explicitly check for all important fields to ensure nothing is missed
            const importantFields: Array<keyof VehicleDetails> = [
              "make",
              "model",
              "variant",
              "year",
              "fuelType",
              "bodyType",
              "transmission",
              "color",
              "engineVolume",
              "horsepower",
              "doors",
              "mileage",
              "vin",
              "regNumber",
              "equipment",
              "registrationDate",
              "lastInspectionDate",
              "annualVehicleTax",
              "seats",
              "owners",
            ];

            importantFields.forEach((key) => {
              const value = initialData[key];
              if (
                value !== undefined &&
                value !== null &&
                (typeof value !== "string" || value !== "") &&
                (typeof value !== "object" ||
                  !Array.isArray(value) ||
                  value.length > 0)
              ) {
                (updates as any)[key] = value;
              }
            });
          } else {
            // Partial sync: only update fields that are missing or different
            // DEBUG: Uncomment for debugging
            // console.log('[useVehicleForm] 🔄 Performing PARTIAL sync - only missing fields');
            (Object.keys(initialData) as Array<keyof VehicleDetails>).forEach(
              (key) => {
                const value = initialData[key];
                if (
                  value !== undefined &&
                  value !== null &&
                  (typeof value !== "string" || value !== "") &&
                  (prev[key] === undefined ||
                    prev[key] === "" ||
                    prev[key] === null)
                ) {
                  (updates as any)[key] = value;
                }
              }
            );
          }

          // DEBUG: Uncomment for debugging updates
          // console.log('[useVehicleForm] 📝 Updates to apply:', updates);

          // Only update if there are actual changes
          if (Object.keys(updates).length > 0) {
            const newState = { ...prev, ...updates };
            // DEBUG: Uncomment for debugging state updates
            // console.log('[useVehicleForm] ✅ Returning updated state:', {
            //   newState: {
            //     make: newState.make,
            //     model: newState.model,
            //     year: newState.year,
            //     fuelType: newState.fuelType,
            //     bodyType: newState.bodyType,
            //     color: newState.color,
            //     transmission: newState.transmission,
            //     variant: newState.variant,
            //     engineVolume: newState.engineVolume,
            //   },
            // });
            return newState;
          }
          // DEBUG: Uncomment for debugging
          // console.log('[useVehicleForm] ⚠️ No updates to apply, returning prev state');
          return prev;
        });
      }, 0);
    }

    prevIsAutoLoadedRef.current = isAutoLoaded;
    prevInitialDataRef.current = initialData;
  }, [isAutoLoaded, initialData]);

  // EXCEPTION 2: Sync transmission and engineVolume from initialData when they change
  // This is needed for AutoUncleVerificationDialog updates to be reflected in the form
  // But only if we've already initialized and AutoUncle data has been loaded
  // IMPORTANT: Use setTimeout to avoid React warnings about setState during render
  useEffect(() => {
    if (hasInitializedRef.current && isAutoLoaded) {
      // Use setTimeout to ensure this happens after render, avoiding React warnings
      setTimeout(() => {
        setVehicleDetails((prev) => {
          const updates: Partial<VehicleDetails> = {};
          // Only sync if initialData has a value and it's different from current state
          if (
            initialData.transmission !== undefined &&
            initialData.transmission !== prev.transmission
          ) {
            updates.transmission = initialData.transmission;
          }
          if (
            initialData.engineVolume !== undefined &&
            initialData.engineVolume !== prev.engineVolume
          ) {
            updates.engineVolume = initialData.engineVolume;
          }
          // Only update if there are actual changes
          if (Object.keys(updates).length > 0) {
            return { ...prev, ...updates };
          }
          return prev;
        });
      }, 0);
    }
  }, [initialData.transmission, initialData.engineVolume, isAutoLoaded]);

  // EXCEPTION 3: Sync extraTireSet from initialData when it changes
  // This is needed to sync checkbox state between Fordonsinfo and Fordonstest sections
  // Works regardless of isAutoLoaded to ensure bidirectional sync
  useEffect(() => {
    if (hasInitializedRef.current) {
      // Use setTimeout to ensure this happens after render, avoiding React warnings
      setTimeout(() => {
        setVehicleDetails((prev) => {
          // Only sync if initialData has a value and it's different from current state
          if (
            initialData.extraTireSet !== undefined &&
            initialData.extraTireSet !== prev.extraTireSet
          ) {
            return { ...prev, extraTireSet: initialData.extraTireSet };
          }
          return prev;
        });
      }, 0);
    }
  }, [initialData.extraTireSet]);

  // Notify parent of changes AFTER state update (to avoid React warning)
  useEffect(() => {
    if (shouldNotifyParentRef.current) {
      shouldNotifyParentRef.current = false;
      onChange(vehicleDetails);
    }
  }, [vehicleDetails, onChange]);

  // Auto-open appropriate section if car was loaded from registration number
  useEffect(() => {
    if (
      isAutoLoaded &&
      initialData.make &&
      initialData.model &&
      initialData.year
    ) {
      const hasAllDetails =
        initialData.fuelType &&
        (initialData.fuelType === "El" ||
          initialData.fuelType === "Elbil" ||
          initialData.fuelType === "ELECTRIC" ||
          initialData.engine) &&
        (initialData.vehicleType === "mc" || initialData.bodyType) &&
        initialData.transmission &&
        initialData.color &&
        initialData.variant;

      if (hasAllDetails) {
        // All details complete - open equipment section if equipment exists, otherwise open it anyway to show
        setOpenSections({
          makeModel: false,
          details: false,
          tires: false,
          equipment: true, // Always open equipment section when details are complete
          final: false,
        });
      } else {
        // Fordonsinformation is complete but Bildetaljer needs color - open details
        setOpenSections({
          makeModel: false, // Close Fordonsinformation
          details: true, // Open Bildetaljer (needs color)
          tires: false,
          equipment: false,
          final: false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoLoaded]);

  const validateMakeModel = () => {
    if (!vehicleDetails.make || !vehicleDetails.model || !vehicleDetails.year) {
      return false;
    }
    // In manual mode, regNumber is required and must be valid format
    if (isManualMode) {
      if (!regNumber) {
        return false;
      }
      if (!validateRegistrationFormatAny(regNumber)) {
        return false;
      }
    }
    return true;
  };

  const validateDetails = useCallback(
    (details: Partial<VehicleDetails> = vehicleDetails) => {
      const requiredFields: (keyof VehicleDetails)[] = [
        "fuelType",
        "transmission",
        "color"
      ];
      // Engine is only required for non-electric vehicles
      const isElectric =
        details.fuelType === "El" || details.fuelType === "Elbil" || details.fuelType === "ELECTRIC";
      if (!isElectric) {
        requiredFields.push("engine");
      }
      // Only require bodyType if vehicleType is not "mc"
      if (details.vehicleType !== "mc") {
        requiredFields.push("bodyType");
      }

      const missingFields = requiredFields.filter((field) => !details[field]);
      return missingFields.length === 0;
    },
    [vehicleDetails]
  );

  const validateEquipment = () => {
    if (!vehicleDetails.equipment || vehicleDetails.equipment.length === 0) {
      return false;
    }
    return true;
  };

  const validateTires = () => {
    if (!vehicleDetails.currentTireType || !vehicleDetails.currentRimType) {
      return false;
    }
    // If extraTireSet is true, require extraTireType and extraRimType
    if (vehicleDetails.extraTireSet === true) {
      if (!vehicleDetails.extraTireType || !vehicleDetails.extraRimType) {
        return false;
      }
    }
    return true;
  };

  const handleSelectChange = (
    value: string | boolean | number | string[],
    key: keyof VehicleDetails
  ) => {
    // Block editing of auto-loaded fields
    // Note: color is NOT blocked because AutoUncle doesn't provide it
    const autoLoadedFields = [
      "make",
      "model",
      "year",
      "fuelType",
      "engine",
      "bodyType",
      "transmission",
      "vehicleType",
    ];
    if (
      isAutoLoaded &&
      autoLoadedFields.includes(key) &&
      onAutoLoadedFieldEditAttempt
    ) {
      onAutoLoadedFieldEditAttempt();
      return;
    }

    // Use functional update to ensure we always have the latest state and update atomically
    setVehicleDetails((prev) => {
      let updated: Partial<VehicleDetails>;

      if (key === "make") {
        // For make, update make and clear ALL dependent fields if make changed
        // This includes: model, engine, AND all Bildetaljer (fuelType, transmission, bodyType, color)
        const prevMake = prev.make;
        // Clear ALL Bildetaljer if make is being cleared (value is empty) OR if make changed from one value to another
        const shouldClearDetails =
          !value || // Make is being cleared
          (prevMake && prevMake !== value && value); // Make changed from one value to another
        updated = {
          ...prev,
          make: value as string,
          // Clear model, engine, and ALL Bildetaljer if make changed or was cleared.
          //
          // variant and trim are cleared for the same reason as engine: they describe the
          // PREVIOUS make's vehicle ("1.6 TDI" / "Ambition" from a Skoda are simply wrong
          // once the dealer corrects the make to Volvo), and leaving them behind is the
          // same wrong-data hazard the engine reset exists to prevent.
          ...(shouldClearDetails
            ? {
                model: "",
                engine: "",
                variant: "",
                trim: "",
                fuelType: "",
                transmission: "",
                bodyType: "",
                color: "",
              }
            : {}),
        };
      } else if (key === "model") {
        // When model changes, clear engine (engine options depend on make+model)
        const prevModel = prev.model;
        updated = {
          ...prev,
          model: value as string,
          // Clear engine if model actually changed from one value to another
          ...(prevModel && prevModel !== value && value ? { engine: "" } : {}),
        };
      } else if (key === "vehicleType") {
        // When vehicleType changes, clear bodyType if changed to MC
        const newVehicleType = value as "car" | "mc";
        updated = {
          ...prev,
          vehicleType: newVehicleType,
          // Clear bodyType if changed to MC
          ...(newVehicleType === "mc" ? { bodyType: "" } : {}),
        };
      } else if (key === "fuelType") {
        // When fuelType is set to El or Elbil, automatically set engine to "Electric"
        const newFuelType = value as string;
        updated = {
          ...prev,
          fuelType: newFuelType,
          // Auto-set engine to "Electric" for electric vehicles
          ...(newFuelType === "El" || newFuelType === "Elbil" || newFuelType === "ELECTRIC"
            ? { engine: "Electric" }
            : // If switching away from El/Elbil and engine is "Electric", clear it
            prev.fuelType === "El" || prev.fuelType === "Elbil" || prev.fuelType === "ELECTRIC"
            ? prev.engine === "Electric"
              ? { engine: "" }
              : {}
            : {}),
        };
      } else {        // For all other fields, just update the field
        updated = {
          ...prev,
          [key]:
            key === "equipment"
              ? Array.isArray(value)
                ? value // If value is already an array, use it directly
                : prev.equipment?.includes(value as string)
                ? prev.equipment.filter((item) => item !== value)
                : [...(prev.equipment || []), value as string]
              : key === "mileage"
              ? typeof value === "string"
                ? parseInt(value, 10) || 0
                : value
              : key === "electricDriveRange" ? parseInt(value as string, 10) || 0 : value,
        };
      }

      // Mark that we should notify parent after state update
      shouldNotifyParentRef.current = true;
      return updated;
    });

    // Auto-advance logic is now handled in useEffect below based on vehicleDetails
  };

  // Auto-advance sections based on completion status - derived from vehicleDetails
  useEffect(() => {
    // Don't auto-advance during initial load or if make/model/year are not set
    if (!vehicleDetails.make || !vehicleDetails.model || !vehicleDetails.year) {
      return;
    }

    // Don't auto-advance if user manually opened a section
    // This prevents auto-close when user clicks edit button
    if (manuallyOpenedSectionRef.current !== null) {
      return;
    }

    const detailsComplete = validateDetails();

    // If year is set and makeModel is open, but details are not complete, open details section
    // This is auto-advance from makeModel to details when year is filled
    if (vehicleDetails.year && openSections.makeModel && !detailsComplete) {
      setOpenSections({
        makeModel: false,
        details: true,
        tires: false,
        equipment: false,
        final: false,
      });
      return;
    }

  }, [
    vehicleDetails.year,
    vehicleDetails.fuelType,
    vehicleDetails.engine,
    vehicleDetails.transmission,
    vehicleDetails.bodyType,
    vehicleDetails.color,
    vehicleDetails.vehicleType,
    vehicleDetails.make,
    vehicleDetails.model,
    openSections.makeModel,
    openSections.details,
    openSections.equipment,
    validateDetails,
  ]);

  const handleNext = (currentSection: keyof FormSections) => {
    if (currentSection === "makeModel" && validateMakeModel()) {
      setOpenSections({
        makeModel: false,
        details: true,
        tires: false,
        equipment: false,
        final: false,
      });
    }
    else if (currentSection === "details" && validateDetails()) {
      setOpenSections({
        makeModel: false,
        details: false,
        tires: false,
        equipment: true,
        final: false,
      });
    }
    else if (currentSection === "equipment" && validateEquipment()) {
      setOpenSections({
        makeModel: false,
        details: false,
        tires: true,
        equipment: false,
        final: false,
      });
    } else if (currentSection === "tires" && validateTires()) {
      setOpenSections({
        makeModel: false,
        details: false,
        tires: false,
        equipment: false,
        final: true,
      });
    }
  };

  const handleEditSection = (
    section: keyof FormSections,
    event: React.MouseEvent
  ) => {
    event.preventDefault();
    event.stopPropagation();

    // Get current state before toggle
    const prevState = openSections[section];
    const willOpen = !prevState;

    // Mark that user manually opened this section
    // Keep the flag set as long as the section is open
    if (willOpen) {
      manuallyOpenedSectionRef.current = section;
    } else {
      // Section is being closed - clear the flag only if it was this section
      if (manuallyOpenedSectionRef.current === section) {
        manuallyOpenedSectionRef.current = null;
      }
    }

    // Toggle the clicked section
    setOpenSections((prev) => {
      const newState = {
        makeModel: section === "makeModel" ? !prev.makeModel : false,
        details: section === "details" ? !prev.details : false,
        tires: section === "tires" ? !prev.tires : false,
        equipment: section === "equipment" ? !prev.equipment : false,
        final: section === "final" ? !prev.final : false,
      };

      // If section was closed manually, clear the flag
      if (!willOpen && manuallyOpenedSectionRef.current === section) {
        manuallyOpenedSectionRef.current = null;
      }

      return newState;
    });
  };

  return {
    vehicleDetails,
    openSections,
    handleSelectChange,
    handleEditSection,
    handleNext,
  };
};
